import { supabase } from "../utils/supabaseClient";
import {
  BillingCycle,
  Invoice,
  OwnerType,
  PaymentMethod,
  PlanTier,
  ResourceAddonPack,
  ResourceSummary,
  ResourceType,
  ResourceUsage,
  SubscriptionAddon,
  SubscriptionRecord,
  SubscriptionStatus,
  SubscriptionSummary,
  SubscriptionUser,
} from "../types/subscription";

// Get plan tiers — direct table query
export async function getAvailablePlans(): Promise<PlanTier[]> {
  const { data, error } = await supabase
    .from("base_plan_tiers")
    .select("id, name, tier_level, description, price_monthly, price_yearly, features, is_active, created_at, updated_at")
    .eq("is_active", true)
    .order("tier_level", { ascending: true });

  if (error) {
    console.error("Error fetching plans:", error);
    throw error;
  }

  return (data ?? []) as PlanTier[];
}

// Get resource types — direct table query
export async function getResourceTypes(): Promise<ResourceType[]> {
  const { data, error } = await supabase
    .from("base_resource_types")
    .select("id, code, name, description, unit_label, value_type, created_at")
    .order("code", { ascending: true });

  if (error) {
    console.error("Error fetching resource types:", error);
    throw error;
  }

  return (data ?? []) as ResourceType[];
}

// Get user subscription — direct table query
export async function getUserSubscription(
  userId?: string
): Promise<SubscriptionRecord | null> {
  try {
    let ownerId = userId;
    if (!ownerId) {
      const { data: { user } } = await supabase.auth.getUser();
      ownerId = user?.id;
    }
    if (!ownerId) return null;

    const { data, error } = await supabase
      .from("base_subscription_records")
      .select("id, owner_id, owner_type, plan_id, status, starts_at, ends_at, created_at, updated_at")
      .eq("owner_id", ownerId)
      .eq("owner_type", "user")
      .maybeSingle();

    if (error) throw error;
    return (data ?? null) as SubscriptionRecord | null;
  } catch (error) {
    console.error("Error fetching user subscription:", error);
    return null;
  }
}

// Get user's primary org membership ID — used to auto-load org subscription for members
export async function getUserOrgId(userId: string): Promise<string | null> {
  try {
    const { data } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", userId)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();
    return data?.organisation_id ?? null;
  } catch {
    return null;
  }
}

// Get organization subscription — uses SECURITY DEFINER RPC (in subscription_complete_schema.sql)
export async function getOrganizationSubscription(
  orgId: string
): Promise<SubscriptionRecord | null> {
  const { data, error } = await supabase.rpc("get_organization_subscription", {
    org_id: orgId,
  });

  if (error) {
    if (error.code === "PGRST116") return null;
    console.error("Error fetching organization subscription:", error);
    throw error;
  }

  if (!data || data.length === 0) return null;
  return data[0] as SubscriptionRecord;
}

// Get resource usage — direct table query
export async function getResourceUsage(
  subscriptionId: string
): Promise<ResourceUsage[]> {
  const { data, error } = await supabase
    .from("base_resource_usage")
    .select("id, subscription_id, resource_type_id, current_usage, last_updated")
    .eq("subscription_id", subscriptionId);

  if (error) {
    console.error("Error fetching resource usage:", error);
    throw error;
  }

  return (data ?? []) as ResourceUsage[];
}

// Get subscription summary — uses SECURITY DEFINER RPC (base_get_subscription_usage_summary)
export async function getSubscriptionSummary(
  subscriptionId: string
): Promise<SubscriptionSummary | null> {
  const { data, error } = await supabase
    .rpc("base_get_subscription_usage_summary", {
      p_subscription_id: subscriptionId,
    });

  if (error) {
    console.error("Error fetching subscription summary:", error);
    throw error;
  }

  return data as SubscriptionSummary;
}

// Get subscription addons — direct table query
export async function getSubscriptionAddons(
  subscriptionId: string
): Promise<SubscriptionAddon[]> {
  const { data, error } = await supabase
    .from("base_subscription_addons")
    .select("id, subscription_id, addon_pack_id, quantity, added_at, status")
    .eq("subscription_id", subscriptionId);

  if (error) {
    console.error("Error fetching subscription addons:", error);
    throw error;
  }

  return (data ?? []) as SubscriptionAddon[];
}

// Get available addon packs — direct table query
export async function getAvailableAddonPacks(): Promise<ResourceAddonPack[]> {
  const { data, error } = await supabase
    .from("base_resource_addon_packs")
    .select("id, name, description, resource_type_id, quantity, price, is_active, created_at")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching addon packs:", error);
    throw error;
  }

  return (data ?? []) as ResourceAddonPack[];
}

// Get subscription payment methods — direct table query
export async function getPaymentMethods(
  subscriptionId: string
): Promise<PaymentMethod[]> {
  const { data, error } = await supabase
    .from("base_payment_methods")
    .select("id, subscription_id, stripe_payment_method_id, type, is_default, created_at")
    .eq("subscription_id", subscriptionId);

  if (error) {
    console.error("Error fetching payment methods:", error);
    throw error;
  }

  return (data ?? []) as PaymentMethod[];
}

// Get subscription invoices — direct table query
export async function getInvoices(subscriptionId: string): Promise<Invoice[]> {
  const { data, error } = await supabase
    .from("base_invoices")
    .select("id, subscription_id, stripe_invoice_id, amount, currency, status, invoice_date, due_date, paid_date, invoice_pdf_url, created_at")
    .eq("subscription_id", subscriptionId)
    .order("invoice_date", { ascending: false });

  if (error) {
    console.error("Error fetching invoices:", error);
    throw error;
  }

  return (data ?? []) as Invoice[];
}

// Create or update subscription — direct insert/upsert
export async function createOrUpdateSubscription(
  subscription: Partial<SubscriptionRecord>
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase
    .from("base_subscription_records")
    .upsert({
      owner_id: subscription.owner_id,
      owner_type: subscription.owner_type,
      plan_id: subscription.plan_id,
      status: subscription.status || "active",
      starts_at: subscription.starts_at || null,
      ends_at: subscription.ends_at || null,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating/updating subscription:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Cancel subscription — direct update
export async function cancelSubscription(
  subscriptionId: string,
  cancelAtPeriodEnd: boolean = true
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase
    .from("base_subscription_records")
    .update({
      status: cancelAtPeriodEnd ? "active" : "cancelled",
      updated_at: new Date().toISOString(),
    })
    .eq("id", subscriptionId)
    .select()
    .single();

  if (error) {
    console.error("Error canceling subscription:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Change subscription plan — direct update
export async function changeSubscriptionPlan(
  subscriptionId: string,
  planId: string
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase
    .from("base_subscription_records")
    .update({ plan_id: planId, updated_at: new Date().toISOString() })
    .eq("id", subscriptionId)
    .select()
    .single();

  if (error) {
    console.error("Error changing subscription plan:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Change billing cycle — direct update (stores in ends_at convention)
export async function changeBillingCycle(
  subscriptionId: string,
  billingCycle: BillingCycle
): Promise<SubscriptionRecord> {
  const { data, error } = await supabase
    .from("base_subscription_records")
    .update({ updated_at: new Date().toISOString() })
    .eq("id", subscriptionId)
    .select()
    .single();

  if (error) {
    console.error("Error changing billing cycle:", error);
    throw error;
  }

  return data as SubscriptionRecord;
}

// Add addon to subscription — direct insert / quantity increment
export async function addSubscriptionAddon(
  subscriptionId: string,
  addonPackId: string,
  quantity: number = 1
): Promise<SubscriptionAddon> {
  // Check for existing active addon
  const { data: existing } = await supabase
    .from("base_subscription_addons")
    .select("id, quantity")
    .eq("subscription_id", subscriptionId)
    .eq("addon_pack_id", addonPackId)
    .eq("status", "active")
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabase
      .from("base_subscription_addons")
      .update({ quantity: existing.quantity + quantity })
      .eq("id", existing.id)
      .select()
      .single();
    if (error) throw error;
    return data as SubscriptionAddon;
  }

  const { data, error } = await supabase
    .from("base_subscription_addons")
    .insert({ subscription_id: subscriptionId, addon_pack_id: addonPackId, quantity, status: "active" })
    .select()
    .single();

  if (error) {
    console.error("Error adding subscription addon:", error);
    throw error;
  }

  return data as SubscriptionAddon;
}

// Remove addon from subscription — direct status update
export async function removeSubscriptionAddon(addonId: string): Promise<void> {
  const { error } = await supabase
    .from("base_subscription_addons")
    .update({ status: "cancelled" })
    .eq("id", addonId);

  if (error) {
    console.error("Error removing subscription addon:", error);
    throw error;
  }
}

// Get subscription users — direct table query
export async function getSubscriptionUsers(
  subscriptionId: string
): Promise<SubscriptionUser[]> {
  const { data, error } = await supabase
    .from("base_subscription_users")
    .select("id, subscription_id, user_id, added_by, status, created_at")
    .eq("subscription_id", subscriptionId);

  if (error) {
    console.error("Error fetching subscription users:", error);
    throw error;
  }

  return (data ?? []) as SubscriptionUser[];
}

// Add user to subscription — direct insert
export async function addUserToSubscription(
  subscriptionId: string,
  userId: string,
  addedBy: string
): Promise<SubscriptionUser> {
  const { data, error } = await supabase
    .from("base_subscription_users")
    .upsert(
      { subscription_id: subscriptionId, user_id: userId, added_by: addedBy, status: "active" },
      { onConflict: "subscription_id,user_id" }
    )
    .select()
    .single();

  if (error) {
    console.error("Error adding user to subscription:", error);
    throw error;
  }

  return data as SubscriptionUser;
}

// Remove user from subscription — direct status update
export async function removeUserFromSubscription(
  subscriptionId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from("base_subscription_users")
    .update({ status: "inactive" })
    .eq("subscription_id", subscriptionId)
    .eq("user_id", userId);

  if (error) {
    console.error("Error removing user from subscription:", error);
    throw error;
  }
}
